
import React, { useEffect, useState } from 'react';
import ReviewerActionGate from '../../../components/ReviewerActionGate';
import { folderCheckText } from './FolderCheckBadge';

const ViewAndIngestModal = ({
  isOpen,
  onClose,
  item,
  onRejectAndDelete,
  onApproveAndIngest,
  onViewDocument, // Provided in KnowledgeBase if needed
  isReviewer = false,
  folderName = null,
  onFolderCheckMove,
  onFolderCheckKeep,
}) => {
  const [isRendered, setIsRendered] = useState(false);
  const [actionState, setActionState] = useState('idle'); // 'idle' | 'ingesting' | 'rejecting' | 'moving'
  const check = item?.folder_check;

  const handleFolderCheck = async (action) => {
    setActionState('moving');
    try {
      await action?.(item);
    } finally {
      setActionState('idle');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsRendered(true), 10);
      setActionState('idle');
    } else {
      setIsRendered(false);
    }
  }, [isOpen]);

  const handleApprove = async () => {
    setActionState('ingesting');
    try {
      await onApproveAndIngest(item);
      onClose(); // Closes immediately, letting background polling take over
    } catch (error) {
      setActionState('idle'); 
    }
  };

  const handleReject = async () => {
    setActionState('rejecting');
    try {
      await onRejectAndDelete(item);
      onClose(); // Closes immediately
    } catch (error) {
      setActionState('idle');
    }
  };

  if (!isOpen && !isRendered) return null;
  const isBusy = actionState !== 'idle';

  return (
    <>
      <div 
        className={`fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isRendered ? "opacity-100" : "opacity-0"}`}
        onClick={!isBusy ? onClose : undefined} 
      />

      <div className={`fixed inset-y-0 right-0 z-[9999] w-[600px] max-w-[70vw] bg-white shadow-2xl flex flex-col font-outfit transform transition-transform duration-300 ease-in-out ${isRendered ? "translate-x-0" : "translate-x-full"}`} onClick={(e) => e.stopPropagation()}>
        
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-[#BB42AE] text-[22px] font-semibold truncate pr-4">
            {item?.name || "Document"}
          </h2>
          <button onClick={!isBusy ? onClose : undefined} disabled={isBusy} className={`text-gray-400 transition-colors ${isBusy ? 'opacity-50' : 'hover:text-gray-600'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {check && (
          <div className="mx-6 mb-4 rounded-xl border border-[#F2A46B] bg-[#FFF1E6] p-4 flex flex-col gap-3">
            <p className="m-0 text-[14px] leading-snug text-[#7C2D12]">
              {folderCheckText(check)}{' '}
              {check.wrong_folder && folderName ? `You're adding it to ${folderName}.` : "It isn't in a folder yet."}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <ReviewerActionGate active={isReviewer} place="bottom">
                <button
                  type="button"
                  onClick={() => handleFolderCheck(onFolderCheckMove)}
                  disabled={isBusy}
                  className="px-4 py-2 rounded-md bg-[#BB42AE] text-white text-[13px] font-semibold hover:bg-[#A33898] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {actionState === 'moving' && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Move to {check.suggested}
                </button>
              </ReviewerActionGate>
              <ReviewerActionGate active={isReviewer} place="bottom">
                <button
                  type="button"
                  onClick={() => handleFolderCheck(onFolderCheckKeep)}
                  disabled={isBusy}
                  className="px-4 py-2 rounded-md border border-[#D4D4D8] bg-white text-[#27272A] text-[13px] font-semibold hover:bg-[#F4F4F5] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {check.wrong_folder && folderName ? `Keep in ${folderName}` : 'Dismiss'}
                </button>
              </ReviewerActionGate>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between px-6 pb-4">
          <h3 className="text-[#333333] font-medium text-[16px]">Summary</h3>
        </div>

        <div className="px-6 pb-6 overflow-y-auto custom-scrollbar flex-1">
          <div className="bg-[#97979712] rounded-xl p-4 text-[#000000B3] text-[15px] leading-relaxed whitespace-pre-wrap font-normal">
            {item?.summary || <span className="italic text-gray-400">No summary available for this document. It may still be processing.</span>}
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 px-6 py-4 bg-white mt-auto ">
          <ReviewerActionGate active={isReviewer} place="top">
            <button
              onClick={handleReject}
              disabled={isBusy}
              className={`px-6 py-2.5 rounded-md border font-semibold transition-all duration-300 text-[14px] w-[160px] flex items-center justify-center gap-2 border-[#D9D9D9] text-[#BB42AE] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionState === 'rejecting' ? (
                <><div className="w-4 h-4 border-2 border-[#BB42AE] border-t-transparent rounded-full animate-spin"></div>Rejecting...</>
              ) : "Reject & Delete"}
            </button>
          </ReviewerActionGate>

          <ReviewerActionGate active={isReviewer} place="top">
            <button
              onClick={handleApprove}
              disabled={isBusy}
              className={`px-6 py-2.5 rounded-md font-semibold transition-all duration-300 text-[14px] w-[180px] flex items-center justify-center gap-2 bg-[#BB42AE] text-white hover:bg-[#A33898] disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {actionState === 'ingesting' ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Ingesting...</>
              ) : "Approve & Ingest"}
            </button>
          </ReviewerActionGate>
        </div>
      </div>
    </>
  );
};

export default ViewAndIngestModal;